import * as React from 'react';
import {
  View,
  Text,
  Linking,
  StyleSheet,
  Dimensions,
  Platform
} from 'react-native';
import { translate } from 'secullum-i18n';
import {
  TextBox,
  Space,
  Button,
  ErrorMessage,
  Message,
  CheckBox,
  getLocale
} from 'secullum-react-native-ui';
import { Form } from '@secullum/react-native-autofocus';
import { getTheme } from '../../shared/modules/layout';
import Api from '../modules/api';
import { primeiraLetraMaisculaCadaPalavra } from '../modules/string';
import { DadosAjuda, DadosFuncionario } from '../modules/types';

interface State {
  campos: DadosAjuda;
  resultado: 'sucesso' | 'erro' | null;
  mensagemGeral: string;
  erros: { [key: string]: string };
  processando: boolean;
  termoLGPD: boolean;
}

interface Props {
  dadosFuncionario?: DadosFuncionario;
  empresaBancoId: string;
  nomeEmpresa: string;
  caminhoTermoPrivacidade: string;
  onClose: () => void;
  onNavegar?: () => void;
  onErroConexao?: (mensagem: string, onTentarNovamente: () => void) => void;
}

const theme = getTheme();

export default class SolicitarAjudaForm extends React.Component<Props, State> {
  state: State = {
    campos: {
      aplicativo: 4,
      celular: '',
      cidade: '',
      codigoCliente: '',
      email: '',
      estado: '',
      idioma: 0,
      nome: '',
      nomeCliente: '',
      numeroIdentificador: '',
      texto: ''
    },
    erros: {},
    mensagemGeral: '',
    resultado: null,
    processando: false,
    termoLGPD: false
  };

  componentDidMount() {
    const { dadosFuncionario, empresaBancoId, nomeEmpresa } = this.props;

    let idioma = 0;
    if (getLocale() === 'en') idioma = 1;
    if (getLocale() === 'es') idioma = 2;

    this.setState({
      campos: {
        ...this.state.campos,
        idioma,
        nome: dadosFuncionario?.nome ?? '',
        email: dadosFuncionario?.email ?? '',
        celular: dadosFuncionario?.celular ?? '',
        cidade: dadosFuncionario?.cidade ?? '',
        estado: dadosFuncionario?.uf ?? '',
        numeroIdentificador: dadosFuncionario?.numeroIdentificador ?? '',
        codigoCliente: empresaBancoId,
        nomeCliente: nomeEmpresa
      }
    });
  }

  handleChange = (campo: string, valor: any) => {
    this.setState(state => ({
      campos: { ...state.campos, [campo]: valor }
    }));
  };

  consistirDados = () => {
    const { campos } = this.state;

    const camposObrigatorios: { [key: string]: string } = {
      nome: campos.nome,
      email: campos.email,
      celular: campos.celular,
      cidade: campos.cidade,
      uf: campos.estado,
      texto: campos.texto
    };

    const erros: { [key: string]: string } = {};

    Object.keys(camposObrigatorios)
      .filter(key => !camposObrigatorios[key])
      .forEach(key => {
        Object.assign(erros, {
          [key]:
            translate(
              'O campo {0} é obrigatório',
              translate(primeiraLetraMaisculaCadaPalavra(key))
            ) + '.'
        });
      });

    this.setState({ erros });

    return Object.keys(erros).length === 0;
  };

  handleEnviar = async () => {
    if (!this.consistirDados()) {
      return;
    }

    const api = new Api();

    this.setState({ processando: true });

    await api.postDadosAjuda(this.state.campos, {
      onSuccess: r =>
        this.setState({
          processando: false,
          resultado: 'sucesso',
          mensagemGeral: r.mensagem
        }),
      onError: err => {
        if (err.name === 'TimeoutError' && this.props.onErroConexao) {
          this.props.onErroConexao(err.message, this.handleEnviar);
        } else {
          this.setState({
            processando: false,
            resultado: 'erro',
            mensagemGeral: err.message
          });
        }
      }
    });
  };

  renderLabel = () => {
    const textTerm = translate(
      'Li e estou de acordo com a # da Secullum Software.'
    );

    const textParts = textTerm.split('#');

    return (
      <>
        <Text>{`${textParts[0]} `}</Text>
        <Text
          style={{ fontSize: 12, color: theme.textColor2 }}
          onPress={() =>
            this.props.caminhoTermoPrivacidade?.length > 0 &&
            Linking.openURL(this.props.caminhoTermoPrivacidade)
          }
          nativeID="solicitar-ajuda-termo-privacidade"
        >
          {translate('Política de Privacidade')}
        </Text>
        <Text>{` ${textParts[1]}`}</Text>
      </>
    );
  };

  render() {
    const { campos, erros, processando, termoLGPD, resultado, mensagemGeral } =
      this.state;

    return (
      <Form focusOn={[TextBox]} style={styles.container}>
        <View style={styles.formLarguraDefinida}>
          <TextBox
            nativeID="solicitar-ajuda-nome"
            label={translate('Nome')}
            value={campos.nome}
            maxLength={100}
            returnKeyType={'next'}
            onChange={nome => this.handleChange('nome', nome)}
          />
          <ErrorMessage message={erros.nome} />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-email"
            label={translate('Email')}
            value={campos.email}
            maxLength={100}
            keyboardType={'email-address'}
            returnKeyType={'next'}
            onChange={email => this.handleChange('email', email)}
          />
          <ErrorMessage message={erros.email} />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-celular"
            label={translate('Celular')}
            value={campos.celular}
            maxLength={20}
            keyboardType={'phone-pad'}
            returnKeyType={'next'}
            onChange={celular => this.handleChange('celular', celular)}
          />
          <ErrorMessage message={erros.celular} />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-cidade"
            label={translate('Cidade')}
            value={campos.cidade}
            maxLength={50}
            returnKeyType={'next'}
            onChange={cidade => this.handleChange('cidade', cidade)}
          />
          <ErrorMessage message={erros.cidade} />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-uf"
            label={translate('UF')}
            value={campos.estado}
            maxLength={2}
            keyboardType={'default'}
            returnKeyType={'next'}
            onChange={estado => this.handleChange('estado', estado)}
          />
          <ErrorMessage message={erros.uf} />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-identificador"
            label={translate('Nº Identificador')}
            value={campos.numeroIdentificador}
            keyboardType={'numeric'}
            returnKeyType={'next'}
            onChange={numeroIdentificador =>
              this.handleChange('numeroIdentificador', numeroIdentificador)
            }
          />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-codigo-empresa"
            label={translate('Código da Empresa')}
            value={campos.codigoCliente}
            keyboardType={'numeric'}
            returnKeyType={'next'}
            onChange={bancoId => this.handleChange('codigoCliente', bancoId)}
          />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-nome-empresa"
            label={translate('Nome da Empresa')}
            value={campos.nomeCliente}
            keyboardType={'default'}
            returnKeyType={'next'}
            onChange={nomeEmpresa =>
              this.handleChange('nomeCliente', nomeEmpresa)
            }
          />
        </View>
        <Space />
        <TextBox
          nativeID="solicitar-ajuda-texto"
          label={translate('Texto')}
          value={campos.texto || ''}
          maxLength={1000}
          multiline={Platform.OS === 'web'}
          keyboardType={'default'}
          returnKeyType={'next'}
          onChange={texto => this.handleChange('texto', texto)}
          style={
            Platform.OS === 'web' ? styles.textBox : styles.formLarguraDefinida
          }
          inputStyle={Platform.OS === 'web' && styles.textBoxInput}
        />
        <ErrorMessage message={erros.texto} />
        <Space />
        <View style={styles.formLarguraDefinida}>
          <CheckBox
            nativeID="solicitar-ajuda-lgpd"
            style={{ maxWidth: Dimensions.get('window').width - 80 }}
            renderLabel={this.renderLabel}
            value={termoLGPD}
            onChange={value => this.setState({ termoLGPD: value })}
          />
          <Space />
          <View style={styles.buttons}>
            <Button
              nativeID="solicitar-ajuda-cancelar"
              text={translate('Cancelar')}
              primary={false}
              style={styles.buttonCancel}
              onPress={this.props.onClose}
              disabled={processando}
            />
            <Space width={20} />
            <Button
              nativeID="solicitar-ajuda-enviar"
              text={translate('Enviar')}
              onPress={this.handleEnviar}
              style={styles.buttonSalvar}
              disabled={processando || !termoLGPD}
            />
          </View>
        </View>
        <Message
          nativeID="solicitar-ajuda-sucesso"
          message={translate(mensagemGeral)}
          visible={resultado === 'sucesso'}
          onRequestClose={() => {
            this.setState({ resultado: null, mensagemGeral: '' });
            this.props.onNavegar && this.props.onNavegar();
          }}
        />
        <Message
          nativeID="solicitar-ajuda-erro"
          type="warning"
          message={mensagemGeral}
          visible={resultado === 'erro'}
          onRequestClose={() =>
            this.setState({ resultado: null, mensagemGeral: '' })
          }
        />
      </Form>
    );
  }
}

const styles = StyleSheet.create({
  buttons: {
    flexDirection: 'row'
  },
  buttonCancel: {
    flex: 1,
    backgroundColor: theme.backgroundColor1
  },
  buttonSalvar: {
    flex: 1,
    backgroundColor: theme.textColor2
  },
  textBox: {
    width: '100%'
  },
  textBoxInput: {
    minHeight: 60
  },
  formLarguraDefinida: {
    width: 350
  },
  container: {
    display: 'flex',
    alignItems: 'center'
  }
});
